import type { TFunction } from 'i18next';

import { errorToastMessage } from '../../components/layout/toast-root/toast';
import { isAuthApiError } from '../../features/auth/api/auth-api';
import { FirebaseErrorCodes } from '../../shared/constants';

export interface IAuthError {
  type: 'firebase' | 'backend';
  code?: string;
  status?: number;
  message?: string;
}

const FIREBASE_CODE_TO_I18N: Record<string, string> = {
  'auth/invalid-credential': 'error.auth/invalid-credential',
  'auth/email-already-in-use': 'error.auth/email-already-in-use',
  'auth/too-many-requests': 'error.auth/too-many-requests',
  'auth/popup-closed-by-user': 'error.auth/popup-closed-by-user',
  'auth/email-exists-use-sign-in': 'signUp.emailExistsUseSignInOrReset',
  'auth/linking-not-supported': 'settings.linkingNotSupported',
  'auth/provider-already-linked': 'settings.providerAlreadyLinked',
  'signUp/backendUserExists': 'signUp.userAlreadyExists',
};

const BE_ERRORS_KEY_PREFIX = 'BE_errors.';

export function normalizeAuthError(error: unknown): IAuthError {
  if (isAuthApiError(error)) {
    const data = error.response?.data as
      | { code?: string; message?: string }
      | undefined;
    return {
      type: 'backend',
      code: typeof data?.code === 'string' ? data.code : undefined,
      status: error.response?.status,
      message: typeof data?.message === 'string' ? data.message : error.message,
    };
  }

  const err = error as { code?: string; message?: string };
  const code = err?.code ?? err?.message;

  if (typeof code === 'string' && FirebaseErrorCodes.includes(code)) {
    return { type: 'firebase', code, message: err?.message };
  }

  return {
    type: 'firebase',
    code: typeof code === 'string' ? code : undefined,
    message: err?.message,
  };
}

export function getAuthErrorTranslationKey(error: unknown): string {
  const normalized = normalizeAuthError(error);

  if (normalized.type === 'backend') {
    if (normalized.code) {
      return BE_ERRORS_KEY_PREFIX + normalized.code;
    }
    if (normalized.status === 403) {
      return 'signUp.userAlreadyExists';
    }
  }

  if (normalized.type === 'firebase' && normalized.code) {
    const key = FIREBASE_CODE_TO_I18N[String(normalized.code)];
    if (key) return key;
    return `error.${normalized.code}`;
  }

  return 'error.somethingWentWrong';
}

export interface IHandleAuthErrorOptions {
  /** Show toast with translated message. Default true. */
  showToast?: boolean;
}

/**
 * Normalizes auth error, shows toast with i18n message, returns translation key.
 */
export function handleAuthError(
  error: unknown,
  t: TFunction,
  options: IHandleAuthErrorOptions = {},
): string {
  const { showToast = true } = options;
  const key = getAuthErrorTranslationKey(error);

  if (showToast) {
    errorToastMessage(t(key));
  }

  return key;
}
